import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "41";
const height = "40";
const viewBox = "0 0 41 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    small: { width: 17, height: 17 },
    medium: { width: 24, height: 24 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.oolong}
            fillRule="evenodd"
            d="M3.817 0h32.746a3.625 3.625 0 013.627 3.627v32.746A3.625 3.625 0 0136.563 40H3.817A3.625 3.625 0 01.19 36.373V3.627A3.625 3.625 0 013.817 0zm9.743 12.58c1.326 0 2.444-1.04 2.444-2.41s-1.118-2.411-2.444-2.411c-1.326 0-2.444 1.041-2.444 2.41 0 1.37 1.118 2.412 2.444 2.412zm-5.063 12.9a8.377 8.377 0 00-.22 1.808c0 2.115 1.151 3.528 3.595 3.528 2.028 0 3.671-1.205 4.855-3.145l-.723 2.904h4.022l2.301-9.227c.57-2.334 1.688-3.55 3.375-3.55 1.326 0 2.16.821 2.16 2.191 0 .395-.034.822-.176 1.293l-1.184 4.241a6.58 6.58 0 00-.252 1.797c0 2.017 1.184 3.485 3.66 3.485 2.116 0 3.814-1.37 4.746-4.635l-1.578-.614c-.79 2.192-1.469 2.586-2.017 2.586-.537 0-.822-.361-.822-1.073 0-.33.077-.68.176-1.118l1.15-4.132c.285-.975.395-1.83.395-2.619 0-3.09-1.874-4.701-4.132-4.701-2.115 0-4.274 1.906-5.359 3.923l.79-3.617H17.11l-.865 3.179h2.871l-1.775 7.09c-1.392 3.09-3.946 3.145-4.263 3.068-.526-.12-.866-.317-.866-.997 0-.394.077-.964.252-1.644l2.696-10.696H8.332l-.865 3.179h2.86l-1.83 7.495z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "ApiCustomerInvisionLogo"
});
